import type { RxCollection } from 'rxdb';
import {
    sanitizeQuotas,
    type NumberQuota,
    type ProjectDocType
} from '@ast-data/shared';

/** Which allocation a quota list is — one per grid on the Kontingente page. */
export type QuotaKind = 'findingQuotas' | 'profileQuotas' | 'findSlipQuotas';

/**
 * The union of a family's quota lists, deduped by range + Bezeichnung. Writes
 * mirror the same array onto every member, so in practice each member already
 * carries the whole list; the union is the tolerant READ — it survives a member
 * that missed a write without ever narrowing the allocation. Divergence
 * resolves on the next save, which rewrites all members.
 */
export function mergeFamilyQuotas(
    lists: readonly (readonly NumberQuota[])[]
): NumberQuota[] {
    const out: NumberQuota[] = [];
    const seen = new Set<string>();
    for (const list of lists) {
        for (const quota of list) {
            const key = `${quota.from}-${quota.to}-${quota.label}`;
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            out.push(quota);
        }
    }
    return out;
}

/**
 * Persist one of a family's quota lists — the single mutation behind add / edit
 * / delete on the Kontingente page. Kontingente are family state and a family
 * has no anchor project, so the sanitized array is mirrored onto every member,
 * the way `syncFamily` maintains `familyId` itself. Members already holding the
 * value are skipped, so a no-op edit doesn't churn the family's updatedAt.
 *
 * Admin/PM-only path; the server enforces CASL on the resulting pushes.
 */
export async function saveQuotas(
    collection: RxCollection<ProjectDocType>,
    projectId: string,
    kind: QuotaKind,
    quotas: readonly NumberQuota[]
): Promise<void> {
    const self = await collection.findOne(projectId).exec();
    if (!self) {
        return;
    }
    const clean = sanitizeQuotas(quotas);
    const encoded = JSON.stringify(clean);
    const all = await collection.find().exec();
    const members = all.filter(
        (doc) =>
            doc.id === projectId ||
            (self.familyId !== '' && doc.familyId === self.familyId)
    );
    for (const member of members) {
        if (JSON.stringify(member[kind] ?? []) === encoded) {
            continue;
        }
        const patch = { [kind]: clean } as Partial<ProjectDocType>;
        await member.incrementalPatch({
            ...patch,
            updatedAt: new Date().toISOString()
        });
    }
}
